import React from 'react';
import { Navigate } from 'react-router-dom';
import { useRole } from '../contexts/RoleContext';
import { ROLE_HIERARCHY } from '../services/roleService';
import { Box, CircularProgress, Container, Typography } from '@mui/material';

/**
 * ProtectedRoute component for role-based access control
 * Uses role hierarchy: admin (3) > handicapper (2) > user (1)
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string} props.requiredRole - The minimum required role to access this route
 * @param {string} props.redirectTo - Where to redirect if unauthorized (default: '/unauthorized')
 */
export function ProtectedRoute({ children, requiredRole, redirectTo = '/unauthorized' }) {
  const { role, loading } = useRole();

  // Show loading state while checking role
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Verifying permissions...
        </Typography>
      </Container>
    );
  }

  // Check if user has sufficient role level
  const userLevel = ROLE_HIERARCHY[role] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;

  if (userLevel < requiredLevel) {
    console.warn(`Access denied: User role '${role}' (level ${userLevel}) does not meet required role '${requiredRole}' (level ${requiredLevel})`);
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}
